<template>
  <Layout
    class="onboarding-splitscreen"
    :class="{ 'onboarding-splitscreen--loaded': riveRef?.isLoaded }"
    :title="page.title"
  >
    <div class="onboarding-splitscreen__top-text">
      <h2 class="onboarding-splitscreen__list-title">Original Book</h2>
      <ul class="onboarding-splitscreen__list">
        <li
          v-for="(item, index) in page.topText"
          :key="index"
          class="onboarding-splitscreen__item"
        >
          {{ item }}
        </li>
      </ul>
    </div>

    <div class="onboarding-splitscreen__book-shadow"></div>

    <div class="onboarding-splitscreen__bottom-text">
      <h2 class="onboarding-splitscreen__list-title">Shortform Guide</h2>
      <ul class="onboarding-splitscreen__list">
        <li
          v-for="(item, index) in page.bottomText"
          :key="index"
          class="onboarding-splitscreen__item"
        >
          {{ item }}
        </li>
      </ul>
    </div>

    <Rive
      ref="riveRef"
      class="onboarding-splitscreen__rive"
      :src="getImage(page.animation.src)"
      fit="Layout"
      @stop="view.showContinueButton()"
    />
  </Layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { getImage } from '@/models/onboarding'
import type { QuestionnaireView } from '@/models/onboarding'
import type { SplitscreenPage } from '@/models/onboarding.types'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const riveRef = ref<InstanceType<typeof Rive> | null>(null)

const page = computed(() => {
  return props.view.currentPage as SplitscreenPage
})
</script>

<style lang="scss">
// To properly center the animation, the we have to
// make the header absolute positioned, so that the
// Rive player can fill the whole screen.
.onboarding-questionnaire__header:has(~ .onboarding-splitscreen) {
  position: absolute;
}
</style>

<style lang="scss" scoped>
.onboarding-splitscreen {
  position: relative;
  // Pad the space that was occupied by the header.
  padding-top: calc(var(--onboarding-padding) * 2);

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
    width: 100%;
    height: 100%;
    @media (max-height: 650px) {
      justify-content: flex-start;
    }
  }

  :deep(.onboarding-layout__text) {
    @include visually-hidden;
  }

  // We can't render shadows in the Rive player,
  // so we have a div with the dimensions of the book
  // in the animation, and render the shadow on it.
  // This element doesn't have a background, so it looks
  // like the shadow is coming from the book in the animation.
  &__book-shadow {
    $book-width: 180px;
    $book-height: 236px;
    width: $book-width;
    height: $book-height;

    // The element itself is just used to add
    // the space between the top and bottom text
    // boxes. It's this :after pseudo-element
    // that actually renders the shadow.
    // Only show shadow when using animation (not static image)
    &:after {
      content: '';
      position: absolute;
      left: calc(50% - ($book-width / 2));
      top: calc(50% - ($book-height / 2));
      width: $book-width;
      height: $book-height;
      border-radius: 24px;
      box-shadow: 0 8px 15px rgba($c-black-2, 0.1);
      opacity: 0;
      transform: scale(0.85);

      // We only start the CSS animation once the
      // Rive animation is done loading, so that they
      // are in sync.
      .onboarding-splitscreen--loaded & {
        opacity: 1;
        transform: scale(1);
        transition:
          scale 0.35s 0.1s,
          opacity 0.5s 0.1s;
      }
    }
  }

  &__top-text,
  &__bottom-text {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__top-text {
    margin-top: 32px;

    @media (max-height: 560px) {
      position: relative;
      z-index: 1;

      &:before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: -1;
        height: calc(100% + 32px);
        backdrop-filter: blur(5px);
        mask: linear-gradient(
          180deg,
          rgba(0, 0, 0, 1) 0%,
          rgba(0, 0, 0, 1) 65%,
          rgba(0, 0, 0, 0) 100%
        );
      }
    }
  }

  &__bottom-text {
    padding: 16px 32px;
    border-radius: 24px;
    border: 2px solid $c-yellow;
    background: $c-white;
    box-shadow: 0 8px 15px rgba($c-black-2, 0.1);
    opacity: 0;
    transform: scale(0.85);
    @media (max-height: 650px) {
      margin-bottom: calc(var(--onboarding-padding) * 2);
    }

    .onboarding-splitscreen--loaded & {
      opacity: 1;
      transform: scale(1);
      transition:
        transform 0.4s 1.2s $e-overshoot,
        opacity 0.5s 1.2s;
    }
  }

  &__list-title {
    margin-bottom: 4px;
    font-size: 18px;
    line-height: 26px;
    font-weight: 500;
    letter-spacing: -0.02em;
  }

  &__list {
    padding: 0;
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    color: $c-black-3;
  }

  // The Rive player is rendered below
  // the rest of the page.
  &__rive {
    position: absolute;
    inset: 0;
    max-width: none;
    z-index: -1;
  }

  ~ :deep(.onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
  }
}
</style>
